import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, NAV_LINKS } from "@/lib/siteData";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (hash) => {
    setOpen(false);
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#121A12]/85 backdrop-blur-xl border-b border-[#E3A21A]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="h-20 flex items-center justify-between">
          <a
            href="#top"
            data-testid="navbar-logo"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("#top");
            }}
            className="flex items-center gap-3 group"
          >
            <div className="bg-white rounded-md p-1.5 flex items-center justify-center transition-transform group-hover:scale-105">
              <img
                src="https://customer-assets.emergentagent.com/job_revenue-systems-14/artifacts/4p9j5b60_Untitled%20design.jpg"
                alt="Digibis Digital by Sam Consolidates"
                className="h-10 w-10 object-contain"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-[17px] tracking-tight text-[#F5F5F0]">
                Digibis<span className="text-[#E3A21A]">.</span>
                <span className="text-[#A8B8A8] font-normal ml-1">Digital</span>
              </span>
              <span className="text-[9px] tracking-[0.25em] uppercase text-[#A8B8A8]/70 font-semibold">
                by Sam Consolidates
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo(l.href);
                }}
                className="text-sm text-[#A8B8A8] hover:text-[#E3A21A] transition-colors font-medium tracking-wide"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BRAND.phoneHref}
              data-testid="navbar-phone"
              className="hidden md:inline-flex items-center gap-2 text-[13px] text-[#A8B8A8] hover:text-[#F5F5F0] transition-colors"
            >
              <Phone className="h-3.5 w-3.5 text-[#E3A21A]" />
              {BRAND.phone}
            </a>
            <Button
              data-testid="navbar-book-call"
              onClick={() => scrollTo("#contact")}
              className="hidden sm:inline-flex bg-[#E3A21A] text-[#121A12] font-semibold hover:bg-[#F0B63A] transition-all shadow-[0_0_24px_rgba(227,162,26,0.25)] rounded-full px-5 h-10"
            >
              Book Free Call
            </Button>

            <button
              data-testid="navbar-mobile-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden h-10 w-10 flex items-center justify-center rounded-md border border-[#E3A21A]/20 text-[#F5F5F0]"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="navbar-mobile-menu"
            className="lg:hidden pb-6 border-t border-[#E3A21A]/10 pt-4"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(l.href);
                  }}
                  className="py-3 px-2 text-[#F5F5F0] hover:text-[#E3A21A] transition-colors border-b border-[#E3A21A]/5"
                >
                  {l.label}
                </a>
              ))}
              <Button
                data-testid="mobile-book-call"
                onClick={() => scrollTo("#contact")}
                className="mt-4 bg-[#E3A21A] text-[#121A12] font-semibold hover:bg-[#F0B63A] rounded-full h-11"
              >
                Book Free Strategy Call
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
